import { Box } from "@chakra-ui/react";
import { Feature, FeatureCollection } from "geojson";
import L from "leaflet";
import { useCallback, useEffect, useRef } from "react";
import { GeoJSON } from "react-leaflet";
import ReactDOMServer from "react-dom/server";
import { PropriedadesFeicao } from "store/modules/consultaprevia/types";
import { pontoRotulo } from "utils/map/rotulo";

type Props = {
  colecao: FeatureCollection;
  /**
   * Muda quando o conteúdo da coleção muda. O <GeoJSON> do react-leaflet lê
   * `data` apenas na montagem — sem trocar a `key` a camada nunca se redesenha.
   */
  chave: string;
  siglaSelecionada: string | null;
  /** Rótulo fixo na mancha. Ligado só para zonas: com tudo ligado vira ruído. */
  rotulosPermanentes?: boolean;
};

const propriedades = (feature?: Feature) =>
  (feature?.properties ?? {}) as Partial<PropriedadesFeicao>;

/**
 * Desenha uma coleção da consulta prévia com o estilo que o próprio backend
 * definiu para cada camada. As cores são dado do plano diretor, não decisão do
 * front — o mapa do cidadão tem que bater com o mapa da legislação.
 */
export const CamadaGeoJson = ({
  colecao,
  chave,
  siglaSelecionada,
  rotulosPermanentes = false
}: Props) => {
  const camadaRef = useRef<L.GeoJSON | null>(null);

  const estilo = useCallback(
    (feature?: Feature) => {
      const props = propriedades(feature);
      const isSelecionada =
        !!siglaSelecionada && props.sigla === siglaSelecionada;

      return {
        color: props.corBorda || "#3182CE",
        // A seleção engrossa a borda em vez de trocar a cor: trocar
        // esconderia justamente a camada que o cidadão foi procurar.
        weight: isSelecionada
          ? (props.espessuraBorda || 1) + 3
          : props.espessuraBorda || 1,
        opacity: 1,
        fillColor: props.corPreenchimento || "#63B3ED",
        fillOpacity: isSelecionada ? 0.65 : 0.4
      };
    },
    [siglaSelecionada]
  );

  // Realce por `setStyle`, e não por remontagem. Trocar a `key` a cada mudança
  // de seleção derrubava e recriava a camada inteira — caro, e o mapa ficava
  // um instante sem paths, engolindo o clique de quem já estava mirando.
  useEffect(() => {
    camadaRef.current?.setStyle(estilo);
  }, [estilo]);

  // Reancora o rótulo fixo num ponto interno da MAIOR mancha da zona.
  //
  // Sozinho, o tooltip permanente se posiciona pelo `getCenter()` do Leaflet,
  // que olha só o primeiro anel do primeiro polígono. Como a consulta prévia
  // entrega uma feição por zona — com todas as manchas fundidas num
  // MultiPolygon —, a sigla ia parar na primeira mancha da união, que não é a
  // maior nem fica onde o cidadão está olhando: na ZN 2 caía num fiapo a ~25 km
  // da mancha principal, e a zona parecia estar sem rótulo.
  //
  // Tem que ser num efeito, e não no `onEachFeature`: lá a camada ainda não
  // entrou no mapa, e `openTooltip` sem `_map` não faz nada. Aqui a instância
  // já está viva — os efeitos do <GeoJSON> filho rodam antes dos deste
  // componente. `chave` na dependência porque a camada é recriada com ela.
  useEffect(() => {
    if (!rotulosPermanentes) return;

    camadaRef.current?.eachLayer((camada) => {
      if (!(camada instanceof L.Polygon)) return;

      const geometria = camada.feature?.geometry;
      const ponto = geometria ? pontoRotulo(geometria) : null;

      if (ponto) camada.openTooltip(ponto);
    });
  }, [chave, rotulosPermanentes]);

  return (
    <GeoJSON
      ref={camadaRef}
      key={chave}
      data={colecao}
      onEachFeature={(feature, layer) => {
        const { sigla, nome, corFonte, corContornoFonte } =
          propriedades(feature);

        if (!sigla && !nome) return;

        const contorno = corContornoFonte || "#000";

        layer.bindTooltip(
          ReactDOMServer.renderToString(
            <Box
              color={corFonte || "#fff"}
              fontSize="md"
              fontWeight="bold"
              textShadow={`-1px -1px 0 ${contorno}, 1px -1px 0 ${contorno}, -1px 1px 0 ${contorno}, 1px 1px 0 ${contorno}, -2px 0 0 ${contorno}, 2px 0 0 ${contorno}, 0 -2px 0 ${contorno}, 0 2px 0 ${contorno}`}
            >
              {sigla || nome}
            </Box>
          ),
          {
            permanent: rotulosPermanentes,
            direction: "center",
            className: "label-only-text"
          }
        );
      }}
      style={estilo}
    />
  );
};
